import React, {useCallback, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import i18next from 'i18next';
import {useTranslation} from 'react-i18next';

import {validateCode} from 'api';
import {BackTo} from 'components/common/BackTo';
import {Stepper} from 'components/ui-kit/Stepper';
import {DocumentReportType} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {
    createDisaggregationDocument,
    disaggregationType$,
    DocumentsCreateGate,
    step$,
} from 'models/documents/create';
import {CodeRequestType, CodeResponseType} from 'types/validation';

import {ProductCodes} from '../common/products';
import {ContentWrapper} from '../common/styled';

import {DisaggregationTypeStep} from './form';
import {Title} from './styled';

export const CreateDisaggregation = () => {
    const {t} = useTranslation();
    const step = useStore(step$);
    const disaggregationType = useStore(disaggregationType$);
    useGate(DocumentsCreateGate);

    const validation = useCallback(
        async (markingCode: string, data: CodeResponseType['info'][]) => {
            const errors: string[] = [];

            try {
                const payload: CodeRequestType = {
                    type: DocumentReportType.DISAGGREGATION,
                    markingCode,
                    disaggregationType,
                    scannedCodes: data.map((item) => item.identityCode),
                };
                const codeData = (await validateCode(payload)).data;
                errors.push(...codeData.errors.map((error) => error.message));

                if (data.find((item) => item.identityCode === codeData.info.identityCode))
                    errors.push(i18next.t('components.scan.errors.double'));

                return {errors, codeData: codeData.info};
            } catch (e) {
                console.error(e);
                errors.push('Invalid code');
                return {errors, codeData: null};
            }
        },
        [disaggregationType],
    );

    const steps = useMemo(
        () => [
            {
                label: t('pages.documents.disaggregation.steps.type'),
                component: <DisaggregationTypeStep />,
            },
            {
                label: t('pages.documents.disaggregation.steps.codes'),
                component: (
                    <ProductCodes
                        createDocument={createDisaggregationDocument}
                        validation={validation}
                    />
                ),
            },
        ],
        [t, validation],
    );

    return (
        <>
            <BackTo to={ROUTE.DOCUMENTS_DOCUMENTS}>{t('pages.documents.back')}</BackTo>
            <ContentWrapper>
                <Title>{t('pages.documents.disaggregation.title')}</Title>
                <Stepper steps={steps} activeStep={step} />
            </ContentWrapper>
        </>
    );
};
